import { checkRefreshToken } from 'Api/ProfileApi';
import { pendingSignOut, signOut } from 'State/User/UserActions';
import { createAuthInstance } from 'Utils/AuthSDK';
import { logError } from 'Utils/Logger';
import { createProfile } from 'Utils/ProfileSDK';
import { getUser, updateRefreshToken } from 'Utils/UserCookie';
import store from '../../state/store';

export const DEFAULT_MAX_API_RETRIES = 3;
export const DEFAULT_MAX_REFRESH_RETRIES = 3;

/* To prevent concurrent requests for new token. */
let reqInProgress = false;

class RefreshTokenHandler {
  init(requestCall) {
    this.maxApiRetries = DEFAULT_MAX_API_RETRIES;
    this.maxRefreshRetries = DEFAULT_MAX_REFRESH_RETRIES;
    this.currentApiRetry = 0;
    this.currentRefreshRetry = 0;
    this.requestCall = requestCall;
  }

  makeRequest() {
    if (this.requestCall) {
      return this.requestCall(this.fetchNewToken.bind(this));
    }
    return Promise.reject('Request call fn not set');
  }

  // borrowed back-off logic from axios-retry
  // https://github.com/softonic/axios-retry/blob/11c2cdbd2188d8085a7d84c6026e5cca3c24f070/es/index.mjs#L77
  getExponentialDelay(retryNum) {
    const delay = Math.pow(2, retryNum) * 100;
    const randomSum = delay * 0.4 * Math.random();
    return delay + randomSum;
  }

  async fetchNewToken() {
    const user = getUser();
    if (!user) {
      this.logoutAndRedirectUser('User not logged in');
      return Promise.reject(new Error('User not logged in'));
    }

    let { refreshToken, accessTokenExpiration } = user;
    try {
      if (reqInProgress) {
        await new Promise((resolve) => setTimeout(resolve, this.getExponentialDelay(this.maxApiRetries)));
        return this.makeRequest(this.fetchNewToken);
      }
      reqInProgress = true;
      const checkRefreshRes = await checkRefreshToken({ accessTokenExpiration, refreshToken });
      if (checkRefreshRes.refreshToken) {
        updateRefreshToken({
          refreshToken: checkRefreshRes.refreshToken,
          accessToken: checkRefreshRes.accessToken,
          accessTokenExpiration: checkRefreshRes.accessTokenExpiration,
        });
        createProfile();
        createAuthInstance();
      }
      reqInProgress = false;
      if (++this.currentApiRetry <= this.maxApiRetries) {
        await new Promise((resolve) => setTimeout(resolve, this.getExponentialDelay(this.maxApiRetries)));
        return this.makeRequest(this.fetchNewToken);
      } else {
        this.logoutAndRedirectUser(new Error('Maximum api retries exceeded'));
        return Promise.reject(new Error('Maximum api retries exceeded'));
      }
    } catch (error) {
      reqInProgress = false;
      if (error.statusCode >= 400 && error.statusCode < 500) {
        this.logoutAndRedirectUser(new Error(error?.message || 'Failure to refresh token'));
        return Promise.reject(new Error(error?.message || 'Failure to refresh token'));
      }
      if (++this.currentRefreshRetry <= this.maxRefreshRetries) {
        await new Promise((resolve) => setTimeout(resolve, this.getExponentialDelay(this.maxRefreshRetries)));
        return this.fetchNewToken(true);
      } else {
        this.logoutAndRedirectUser(new Error(error?.message || 'Maximum refresh token retries exceeded'));
        return Promise.reject(new Error(error?.message || 'Maximum refresh token retries exceeded'));
      }
    }
  }

  async logoutAndRedirectUser(error) {
    const user = getUser();
    // if user already logged out, do nothing
    if (store.getState().user?.pendingSignOut || !user) return;
    logError(error);
    try {
      await store.dispatch(pendingSignOut());
      await store.dispatch(signOut());
    } catch (e) {
      console.error(e, 'Unable to sign out user');
    }
  }
}

export const createApiTokenHandler = (request, maxApiRetries) => {
  const tokenHandler = new RefreshTokenHandler();
  tokenHandler.init(request, maxApiRetries);
  return tokenHandler.makeRequest();
};
